import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import { listMyThreads } from "@/lib/messages-page.functions";
import { MessageSquare, Search, Eye } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/messages/")({
  head: () => ({ meta: [{ title: "Messages — Want To Buy" }] }),
  component: MessagesPage,
});

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function MessagesPage() {
  const [q, setQ] = useState("");
  const fetchThreads = useServerFn(listMyThreads);
  const { data, isLoading } = useQuery({
    queryKey: ["messages", "threads"],
    queryFn: () => fetchThreads(),
  });

  const threads = (data?.threads ?? []).filter((t) => {
    if (!q) return true;
    const hay = `${t.counterparty_username} ${t.counterparty_display_name ?? ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Messages</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
              <MessageSquare className="h-3 w-3" /> Threads are scoped to a single transaction — no cold DMs
            </span>
          </div>
        </div>
      </div>
      <div className="relative mt-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by seller, buyer, or card…"
          className="h-10 w-full rounded-md border border-border bg-panel pl-9 pr-3 text-sm focus:border-primary focus:outline-none"
        />
      </div>
      <div className="mt-4 flex items-start gap-2 rounded-md border border-border bg-panel/40 px-3 py-2 text-[11px] text-muted-foreground">
        <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        Messages may be reviewed for policy compliance and dispute resolution.
      </div>

      <div className="panel mt-6 divide-y divide-border">
        {isLoading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && threads.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No conversations yet — message threads are created automatically when you accept an offer or buy from a seller.
          </div>
        )}
        {threads.map((t) => {
          const name = t.counterparty_display_name || t.counterparty_username;
          return (
            <Link
              key={t.id}
              to="/messages/$threadId"
              params={{ threadId: t.id }}
              className="flex gap-3 p-4 transition hover:bg-muted/40"
            >
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                {t.order?.image_url ? (
                  <img
                    src={t.order.image_url}
                    alt={t.order.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center font-display text-sm font-bold text-primary">
                    {name.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {t.order && (
                      <div className="truncate text-sm font-semibold text-foreground">
                        {t.order.title}
                      </div>
                    )}
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="truncate">@{t.counterparty_username}</span>
                      {t.unread_count > 0 && (
                        <span className="inline-flex items-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {timeAgo(t.last_message_at)}
                  </div>
                </div>
                <div className="mt-1 truncate text-sm text-foreground/80">
                  {t.last_message ?? <span className="italic text-muted-foreground">No messages yet</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </AppShell>
  );
}
